import { PlusIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { TrustAccountEntryModal, TrustLedgerGrid } from 'components/TrustLedgerGrid'
import {
  type ITrustAccount,
  type ITrustAccountLedgerResponse,
  type ITrustAccountLedgerRow,
  TRUST_ACCOUNT_PURPOSES,
} from 'components/TrustLedgerGrid/types'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  backfillTrustAccountLedgerRows,
  deleteTrustAccount,
  getBankDirectoryCount,
  getTrustAccountLedger,
  getTrustAccounts,
  importBankDirectory,
  transferBetweenTrustAccounts,
} from 'services'
import { Button } from 'stories/components'
import { confirm, getPrice3decimal } from 'utils'

import TrustAccountModal from './TrustAccountModal'
import TrustAccountTransferModal from './TrustAccountTransferModal'

/**
 * Trust accounts and the ledger struck over each one.
 *
 * The list is the definition of where trust money can sit; picking an account opens every
 * transaction that settled in it, across every loan and party, with its running balance.
 */
export const TrustAccounts = () => {
  const [loading, setLoading] = useState(false)
  const [accounts, setAccounts] = useState<ITrustAccount[]>([])
  const [selected, setSelected] = useState<ITrustAccount | null>(null)
  const [editing, setEditing] = useState<ITrustAccount | null | undefined>(undefined)
  const [entryRow, setEntryRow] = useState<ITrustAccountLedgerRow | null | undefined>(undefined)
  const [transferOpen, setTransferOpen] = useState(false)
  // The balance the ledger struck, so the entry modal can tell whether a withdrawal is covered.
  const [totals, setTotals] = useState<ITrustAccountLedgerResponse['totals']>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [bankDirectoryCount, setBankDirectoryCount] = useState(0)
  const bankDirectoryInput = useRef<HTMLInputElement>(null)

  const load = () => {
    setLoading(true)
    getTrustAccounts({ orderBy: 'name', orderDir: '1' })
      .then((res: any) => {
        const results: ITrustAccount[] = res?.results || []
        setAccounts(results)
        // Keep the open ledger pointed at the same account after an edit.
        setSelected((prev) => (prev ? results.find((item) => item.id === prev.id) || null : null))
      })
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  useEffect(() => {
    getBankDirectoryCount()
      .then((res) => setBankDirectoryCount(res?.count || 0))
      .catch(() => setBankDirectoryCount(0))
  }, [])

  /**
   * Load the routing-number directory that fills in the bank name wherever bank details are
   * entered. The Fed publishes it at frbservices.org as `FedACHdir.txt`; a
   * `routingNumber,bankName` CSV works too.
   */
  const onImportBankDirectory = async (file?: File) => {
    if (!file) return
    setLoading(true)
    try {
      const { imported, skipped } = await importBankDirectory(await file.text())
      setBankDirectoryCount(await getBankDirectoryCount().then((res) => res?.count || 0))
      toast(`Loaded ${imported} routing number${imported === 1 ? '' : 's'}. ${skipped} line${
        skipped === 1 ? '' : 's'
      } skipped.`, { type: 'success' })
    } catch (error: any) {
      toast(error?.message || 'Could not read that file.', { type: 'error' })
    }
    setLoading(false)
  }

  const onDelete = async (account: ITrustAccount) => {
    const result = await confirm(
      `Remove "${account.name}"? An account that money has already moved through is deactivated rather than deleted, so its ledger stays intact.`,
    )
    if (!result) return
    setLoading(true)
    await deleteTrustAccount(account.id).catch(() => null)
    if (selected?.id === account.id) setSelected(null)
    load()
  }

  const onBackfill = async () => {
    const preview = await backfillTrustAccountLedgerRows({ dryRun: true })
    const total = (preview?.stampedLedger || 0) + (preview?.stampedInterest || 0)
    const result = await confirm(
      `Stamp the trust account onto ${total} ledger row${total === 1 ? '' : 's'} written before trust accounts ` +
        `existed, across ${preview?.loans || 0} loan${preview?.loans === 1 ? '' : 's'}? ` +
        `${preview?.unresolved || 0} row${preview?.unresolved === 1 ? '' : 's'} resolve to no account and stay as they are.`,
    )
    if (!result) return
    setLoading(true)
    await backfillTrustAccountLedgerRows({}).catch(() => null)
    setRefreshKey((key) => key + 1)
    setLoading(false)
  }

  return (
    <div className="relative">
      <LayoutLoading show={loading} />

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">Trust Accounts</h2>
        <div className="flex items-center gap-3">
          <input
            ref={bankDirectoryInput}
            type="file"
            accept=".txt,.csv"
            className="hidden"
            onChange={(e) => {
              onImportBankDirectory(e.target.files?.[0])
              e.target.value = ''
            }}
          />
          <button
            className="text-[14px] text-shade-blue hover:underline"
            title="Load the Federal Reserve FedACH participant file (FedACHdir.txt) so the bank name fills itself in from a routing number."
            onClick={() => bankDirectoryInput.current?.click()}
          >
            Bank directory ({bankDirectoryCount.toLocaleString()})
          </button>
          <button className="text-[14px] text-shade-blue hover:underline" onClick={onBackfill}>
            Backfill existing ledger rows
          </button>
          <button className="text-[14px] text-shade-blue hover:underline" onClick={() => setTransferOpen(true)}>
            Transfer between accounts
          </button>
          <Button onClick={() => setEditing(null)}>
            <span className="flex items-center gap-2">
              <PlusIcon className="w-4 h-4" /> Add Trust Account
            </span>
          </Button>
        </div>
      </div>

      <div className="overflow-auto shadow-md rounded mb-6">
        <table className="w-full text-[14px]">
          <thead className="font-medium text-gray-600 bg-gray-100">
            <tr className="text-left">
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Used For</th>
              <th className="px-4 py-2">Whose</th>
              <th className="px-4 py-2">Bank</th>
              <th className="px-4 py-2">Account</th>
              <th className="px-4 py-2">GL</th>
              <th className="px-4 py-2 text-right">Opening Balance</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account, index) => (
              <tr
                key={account.id}
                className={`${index % 2 ? 'bg-gray-50' : ''} ${
                  selected?.id === account.id ? 'bg-shade-blue/5' : ''
                } cursor-pointer hover:bg-shade-blue/5`}
                onClick={() => setSelected(account)}
              >
                <td className="px-4 py-2">
                  <span className={account.isActive ? '' : 'text-gray-400 line-through'}>{account.name}</span>
                  {account.isDefault && (
                    <span className="ml-2 rounded px-2 py-0.5 text-[11px] bg-slate-100 text-slate-700">default</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {(account.purposes || []).map((purpose) => TRUST_ACCOUNT_PURPOSES[purpose] || purpose).join(', ')}
                </td>
                <td className="px-4 py-2">{account.ownerType}</td>
                <td className="px-4 py-2">{account.bankName}</td>
                <td className="px-4 py-2">{account.accountNoMasked}</td>
                <td className="px-4 py-2">{account.glAccountNo}</td>
                <td className="px-4 py-2 text-right">${getPrice3decimal(account.openingBalance)}</td>
                <td className="px-4 py-2 whitespace-nowrap text-right">
                  <button
                    className="text-shade-blue hover:underline mr-3"
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditing(account)
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="text-red-600 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(account)
                    }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {!accounts.length && !loading && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={8}>
                  No trust accounts yet. Add one to start recording where trust money settles.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!!selected && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-variation-settings-600">{selected.name} - Ledger</h3>
            <button className="text-[14px] text-shade-blue hover:underline" onClick={() => setEntryRow(null)}>
              Add transaction
            </button>
          </div>
          <TrustLedgerGrid
            key={selected.id}
            refreshKey={refreshKey}
            fetchRows={(filters) => getTrustAccountLedger(selected.id, filters)}
            onSelectRow={(row) => setEntryRow(row)}
            onTotals={setTotals}
            emptyText="Nothing has settled in this account yet"
          />
        </div>
      )}

      {editing !== undefined && (
        <TrustAccountModal
          account={editing}
          onClose={(changed: boolean) => {
            setEditing(undefined)
            if (changed) load()
          }}
        />
      )}

      {entryRow !== undefined && (
        <TrustAccountEntryModal
          accounts={accounts}
          row={entryRow}
          defaults={{ trustAccountId: selected?.id }}
          balances={totals}
          onClose={(changed: boolean) => {
            setEntryRow(undefined)
            if (changed) setRefreshKey((key) => key + 1)
          }}
        />
      )}

      {transferOpen && (
        <TrustAccountTransferModal
          accounts={accounts}
          onSubmit={transferBetweenTrustAccounts}
          onClose={(changed: boolean) => {
            setTransferOpen(false)
            if (changed) setRefreshKey((key) => key + 1)
          }}
        />
      )}
    </div>
  )
}
